import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Empty, Input, Space, Spin, Typography } from 'antd';
import { CheckOutlined, ClusterOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  EvaluationModelRole,
  formatRates,
  getRoleModels,
  type EvaluationModelOption,
  type EvaluationModelOptions,
} from '@/api/scenarioIntelligence';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// ModelPickerDrawer — Scenario Intelligence model roles (plan §6, D-SI-9; mockups 02 and 06 frame B).
// A picker is always a drawer, stacked over whatever opened it. Rows are REGISTERED DEPLOYMENTS of the practice (and the
// platform-wide ones), never model families. The server decides which rows can serve the role, their order (cheapest
// first, unpriced last) and every reason; this drawer only shows them. A missing rate reads "Price unavailable", never $0.
// ─────────────────────────────────────────────────────────────────────────────

export const ROLE_TONE: Record<EvaluationModelRole, 'violet' | 'amber' | 'ink'> = {
  [EvaluationModelRole.Scenario]: 'violet',
  [EvaluationModelRole.Critic]: 'amber',
  [EvaluationModelRole.Evaluator]: 'ink',
};

export const ModelPickerDrawer = observer(function ModelPickerDrawer({
  open,
  onClose,
  role,
  merchantId,
  practiceLabel,
  selectedId,
  onSelect,
  title,
  allowOff = false,
}: {
  open: boolean;
  onClose: () => void;
  role: EvaluationModelRole;
  merchantId: number | null;
  practiceLabel?: string;
  /** The ai.AIModel row currently chosen, if any. */
  selectedId?: number | null;
  /** Null = "Off" (only offered when allowOff). */
  onSelect: (option: EvaluationModelOption | null) => void;
  title?: string;
  allowOff?: boolean;
}) {
  const { themeStore, studioModuleStore } = useStore();
  const navigate = useNavigate();
  const isDark = themeStore.mode === 'dark';
  const [options, setOptions] = useState<EvaluationModelOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const request = useRef(0);

  useEffect(() => {
    if (!open) return;
    const mine = ++request.current;
    setOptions(null);
    setError(null);
    setQuery('');
    if (merchantId == null) {
      setError('Choose a practice first: a model role runs on the practice’s own registered deployments.');
      return;
    }
    getRoleModels(role, merchantId)
      .then((o) => mine === request.current && setOptions(o))
      .catch((e: Error) => mine === request.current && setError(e.message));
  }, [open, role, merchantId]);

  const term = query.trim().toLowerCase();
  const matches = (o: EvaluationModelOption) =>
    !term || `${o.deployment ?? ''} ${o.name} ${o.title ?? ''} ${o.provider ?? ''} ${o.apiTitle ?? ''}`.toLowerCase().includes(term);
  const usable = useMemo(() => (options?.usable ?? []).filter(matches), [options, term]); // eslint-disable-line react-hooks/exhaustive-deps
  const notUsable = useMemo(() => (options?.notUsable ?? []).filter(matches), [options, term]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (o: EvaluationModelOption | null) => {
    onSelect(o);
    onClose();
  };

  const headerBg = isDark ? colors.slate[800] : '#FFFFFF';
  const sectionBg = isDark ? colors.slate[900] : colors.slate[50];
  const borderColor = isDark ? colors.slate[700] : colors.slate[200];
  const mutedColor = isDark ? colors.slate[400] : colors.slate[500];
  const roleLabel = options?.roleLabel ?? `${EvaluationModelRole[role]} model`;
  const canOpenModels = studioModuleStore.isLinkDiscoverable('/models');

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(500px, 100vw)"
      closable={false}
      title={null}
      destroyOnHidden
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' }, header: { display: 'none' } }}
    >
      <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${borderColor}`, background: headerBg }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <ClusterOutlined style={{ fontSize: 16, color: colors.ink[600] }} />
          <Text strong style={{ fontSize: 15 }}>{title ?? `Choose the ${roleLabel}`}</Text>
          <StatusPill tone={ROLE_TONE[role]}>{roleLabel}</StatusPill>
        </div>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0 10px' }}>
          {`Registered deployments of ${practiceLabel ?? (merchantId != null ? `practice #${merchantId}` : 'the practice')} and the platform-wide ones, cheapest first.`}
        </Text>
        <Input
          allowClear
          placeholder="Search deployments by name or provider…"
          prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={() => usable.length === 1 && pick(usable[0])}
        />
      </div>

      <div style={{ flex: 1, overflow: 'auto', background: sectionBg, padding: '8px 0 12px' }}>
        {options?.rule && (
          <div style={{ padding: '4px 16px 8px' }}>
            <Alert type="info" showIcon title={`How the ${roleLabel} is chosen`} description={options.rule} />
          </div>
        )}
        {options?.availabilityNote && (
          <div style={{ padding: '0 16px 8px' }}>
            <Alert type="warning" showIcon title={options.availabilityNote} />
          </div>
        )}
        {error && (
          <div style={{ padding: '8px 16px' }}>
            <Alert type="error" showIcon title="Could not list deployments" description={error} />
          </div>
        )}
        {!options && !error && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin />
          </div>
        )}

        {allowOff && options && (
          <ModelRow
            isDark={isDark}
            selected={selectedId == null}
            onClick={() => pick(null)}
            title="Off"
            meta={`No ${roleLabel.toLowerCase()}: nothing runs in this role.`}
          />
        )}

        {options && usable.length === 0 && notUsable.length === 0 && (
          <div style={{ padding: 24 }}>
            <Empty description={term ? `No deployment matches “${query.trim()}”.` : 'No deployment in this practice can serve this role. An administrator adds or fixes one in Administration → Models.'} />
          </div>
        )}

        {usable.map((o) => (
          <ModelRow key={o.aIModelId} option={o} isDark={isDark} selected={o.aIModelId === selectedId} onClick={() => pick(o)} />
        ))}

        {notUsable.length > 0 && (
          <>
            <div style={{ padding: '14px 20px 4px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: mutedColor }}>
              {`Cannot serve this role · ${notUsable.length}`}
            </div>
            {notUsable.map((o) => (
              <ModelRow key={o.aIModelId} option={o} isDark={isDark} selected={false} />
            ))}
          </>
        )}
      </div>

      <div style={{ padding: '10px 20px', borderTop: `1px solid ${borderColor}`, background: headerBg }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          {canOpenModels ? (
            <Button type="link" style={{ paddingInline: 0 }} onClick={() => navigate('/models')}>
              Manage deployments in Administration → Models
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={onClose}>Close</Button>
        </Space>
      </div>
    </Drawer>
  );
});

function ModelRow({
  option,
  title,
  meta,
  isDark,
  selected,
  onClick,
}: {
  option?: EvaluationModelOption;
  title?: string;
  meta?: string;
  isDark: boolean;
  selected: boolean;
  onClick?: () => void;
}) {
  const [hover, setHover] = useState(false);
  const clickable = !!onClick;
  const baseBg = isDark ? colors.slate[900] : colors.slate[50];
  const hoverBg = isDark ? colors.slate[800] : '#FFFFFF';
  const selectedBg = isDark ? colors.ink[700] + '33' : colors.ink[50];
  const textColor = isDark ? colors.slate[50] : colors.slate[900];
  const mutedColor = isDark ? colors.slate[400] : colors.slate[500];
  const rates = option ? formatRates(option) : null;

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-disabled={clickable ? undefined : true}
      onClick={onClick}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 20px',
        cursor: clickable ? 'pointer' : 'default',
        background: selected ? selectedBg : hover && clickable ? hoverBg : baseBg,
        borderLeft: selected ? `3px solid ${colors.ink[600]}` : '3px solid transparent',
        opacity: option && !option.usable ? 0.72 : 1,
        transition: 'background 80ms',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: textColor }}>{title ?? option?.deployment ?? option?.name}</span>
          {option?.qualification && (
            <StatusPill tone={option.qualification === 'Qualifying' ? 'amber' : 'slate'}>{option.qualification}</StatusPill>
          )}
          {option?.isCurrent && <StatusPill tone="ink">Current</StatusPill>}
        </div>
        <div style={{ fontSize: 12, color: mutedColor, marginTop: 2, overflowWrap: 'anywhere' }}>
          {meta ?? describe(option!)}
        </div>
        {option && option.usable && <Capabilities option={option} isDark={isDark} />}
        {option && !option.usable && (
          <div style={{ fontSize: 12, color: isDark ? colors.red[100] : colors.red[700], marginTop: 4 }}>{option.problems.join(' ')}</div>
        )}
      </div>
      {option && (
        <div style={{ textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          {rates ? (
            <>
              <div style={{ fontSize: 12.5, color: textColor }}>{rates}</div>
              <div style={{ fontSize: 11, color: mutedColor }}>per 1M in / out</div>
            </>
          ) : (
            <StatusPill tone="slate">Price unavailable</StatusPill>
          )}
        </div>
      )}
      {selected && <CheckOutlined style={{ color: colors.ink[600], fontSize: 14, marginTop: 3 }} />}
    </div>
  );
}

function describe(o: EvaluationModelOption): string {
  const scope = o.merchantId == null ? 'every practice' : `practice #${o.merchantId}`;
  const parts = [o.apiTitle, o.provider && o.provider !== o.apiTitle ? o.provider : null, scope];
  if (o.deployment && o.name && o.deployment !== o.name) parts.unshift(`model ${o.name}`);
  return parts.filter(Boolean).join(' · ');
}

function Capabilities({ option, isDark }: { option: EvaluationModelOption; isDark: boolean }) {
  const chip = (text: string, key: string) => (
    <span
      key={key}
      style={{
        display: 'inline-block',
        fontSize: 11,
        padding: '0 7px',
        borderRadius: 999,
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        color: isDark ? colors.slate[300] : colors.slate[600],
        background: isDark ? colors.slate[800] : '#FFFFFF',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
  const chips = [
    chip(`Structured output: ${option.structuredOutputSupport.toLowerCase()}`, 'so'),
    chip(`Tools: ${option.toolsSupport.toLowerCase()}`, 'tools'),
    chip(option.supportsTemperature ? 'Temperature settable' : 'Temperature fixed by the model', 'temp'),
    option.inFoundryProject === true
      ? chip('In the Foundry project ✓', 'foundry')
      : option.inFoundryProject == null
        ? chip('Project not checked', 'foundry')
        : null,
    option.retirementDaysUntil != null && option.retirementDaysUntil <= 180
      ? chip(`Retires in ${option.retirementDaysUntil} days`, 'retire')
      : null,
  ].filter(Boolean);
  return <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>{chips}</div>;
}

/** The one-line summary of a role's model for drawers and cards: "gpt-6-luna · Azure OpenAI". */
export function modelLine(o: Pick<EvaluationModelOption, 'deployment' | 'name' | 'apiTitle'> | null | undefined): string | null {
  if (!o) return null;
  return [o.deployment ?? o.name, o.apiTitle].filter(Boolean).join(' · ');
}

